import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection, getDocs, doc, setDoc, getDoc,
  updateDoc, deleteDoc, serverTimestamp,
  onSnapshot, writeBatch, increment
} from "firebase/firestore";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import Modal from "../../components/Modal";
import UISwitch from "../../components/UISwitch";
import { useLanguage } from "../../context/LanguageContext";
import {
  Truck,
} from "lucide-react";
import { GOOGLE_MAPS_KEY, GOOGLE_MAPS_LIBRARIES } from "../../utils/googleMapsConfig";
const MAP_CENTER = { lat: 25.2048, lng: 55.2708 };

const MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9e8f5" }] },
];

function VendorsManager({ companyId }) {
  const { t } = useLanguage();
  const [vendors, setVendors]         = useState([]);
  const [sections, setSections]       = useState([]);
  const [maxVendors, setMaxVendors]   = useState(null); // null = no limit
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [error, setError]             = useState("");
  const [success, setSuccess]         = useState("");
  const [saving, setSaving]           = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editingVendor, setEditing]   = useState(null);

  // ── Delete state ──────────────────────────────────────────────────────────
  const [deleteModal, setDeleteModal]         = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [deleting, setDeleting]               = useState(false);

  // ── Multi-select state ────────────────────────────────────────────────────
  const [selected, setSelected]     = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  // ── Map state ─────────────────────────────────────────────────────────────
  const [showMap, setShowMap]               = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [liveLocations, setLiveLocations]   = useState({});

  const emptyForm = {
    first_name: "", last_name: "", email: "", password: "",
    phone: "", imei: "", plate: "", sections: [], notes: "", active: true, off_days: [],
  };
  const [form, setForm] = useState(emptyForm);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [vendorsSnap, sectionsSnap, companySnap] = await Promise.all([
        getDocs(collection(db, "companies", companyId, "users")),
        getDocs(collection(db, "companies", companyId, "sections")),
        getDoc(doc(db, "companies", companyId)),
      ]);
      const allUsers = vendorsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setVendors(allUsers.filter(u => u.role === "vendor"));
      setSections(sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      if (companySnap.exists()) {
        const mv = companySnap.data().max_vendors;
        setMaxVendors(mv != null ? mv : null);
      }
    } catch (err) {
      setError("Failed to load: " + err.message);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (!showMap) return;
    const unsub = onSnapshot(collection(db, "companies", companyId, "events"), (snap) => {
      const locations = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.vendor_id && data.lat && data.lng) {
          if (!locations[data.vendor_id] ||
            (data.timestamp?.toMillis?.() > locations[data.vendor_id].timestamp?.toMillis?.())) {
            locations[data.vendor_id] = { lat: data.lat, lng: data.lng, timestamp: data.timestamp, speed: data.speed || 0 };
          }
        }
      });
      setLiveLocations(locations);
    });
    return () => unsub();
  }, [showMap, companyId]);

  function flash(msg, isError = false) {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(""); setSuccess(""); }, 4000);
  }

  function openCreate() { setEditing(null); setForm(emptyForm); setShowPassword(false); setModalOpen(true); }

  function openEdit(vendor) {
    setEditing(vendor);
    setForm({
      first_name: vendor.first_name || "", last_name: vendor.last_name || "",
      email: vendor.email || "", password: "",
      phone: vendor.phone || "", imei: vendor.imei || "", plate: vendor.plate || "",
      sections: vendor.sections || [], notes: vendor.notes || "", active: vendor.active !== false,
      off_days: vendor.off_days || [],
    });
    setShowPassword(false);
    setModalOpen(true);
  }

  function closeModal() { setModalOpen(false); setEditing(null); setForm(emptyForm); }

  function toggleSection(sectionId) {
    setForm(prev => ({
      ...prev,
      sections: prev.sections.includes(sectionId)
        ? prev.sections.filter(s => s !== sectionId)
        : [...prev.sections, sectionId],
    }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    if (!form.first_name.trim()) return setError("First name is required.");
    if (!form.email.trim())      return setError("Email is required.");
    if (!form.imei.trim())       return setError("IMEI is required.");
    if (!form.plate.trim())      return setError("Plate number is required.");
    if (!editingVendor && !form.password.trim()) return setError("Password is required for new vendors.");

    setSaving(true);
    try {
      // Enforce max_vendors limit on new vendor creation
      if (!editingVendor && maxVendors != null) {
        const activeVendors = vendors.filter(v => v.active !== false).length;
        if (activeVendors >= maxVendors) {
          setError(`Vendor limit reached (max: ${maxVendors}). Contact your super admin to increase the limit.`);
          setSaving(false);
          return;
        }
      }

      const data = {
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        email: form.email.trim(), phone: form.phone.trim(),
        imei: form.imei.trim(), plate: form.plate.trim(),
        sections: form.sections, notes: form.notes.trim(),
        active: form.active, role: "vendor", updated_at: serverTimestamp(),
        off_days: form.off_days || [],
      };
      if (form.password.trim()) data.password = form.password.trim();

      if (editingVendor) {
        await updateDoc(doc(db, "companies", companyId, "users", editingVendor.id), data);
        setVendors(prev => prev.map(v => v.id === editingVendor.id ? { ...v, ...data, password: undefined } : v));
        flash("✅ Vendor updated!");
      } else {
        const vendorId = "vendor-" + Date.now().toString(36);
        data.created_at = serverTimestamp();
        await setDoc(doc(db, "companies", companyId, "users", vendorId), data);
        try {
          await updateDoc(doc(db, "companies", companyId), { vendor_count: increment(1) });
        } catch (e) { console.error("[COUNTER] vendor_count increment failed:", e.message); }
        setVendors(prev => [...prev, { id: vendorId, ...data, password: undefined }]);
        flash("✅ Vendor created! Login account being set up...");
      }
      closeModal();
    } catch (err) { setError("Error: " + err.message); }
    setSaving(false);
  }

  async function toggleActive(vendor, value) {
    try {
      await updateDoc(doc(db, "companies", companyId, "users", vendor.id), { active: value, updated_at: serverTimestamp() });
      setVendors(prev => prev.map(v => v.id === vendor.id ? { ...v, active: value } : v));
    } catch (err) { flash("Error: " + err.message, true); }
  }

  // ── Single delete ─────────────────────────────────────────────────────────
  function askDelete(vendor) { setDeleteTarget(vendor); setDeleteModal(true); }

  async function confirmSingleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "companies", companyId, "users", deleteTarget.id));
      try {
        await updateDoc(doc(db, "companies", companyId), { vendor_count: increment(-1) });
      } catch (e) { console.error("[COUNTER] vendor_count decrement failed:", e.message); }
      setVendors(prev => prev.filter(v => v.id !== deleteTarget.id));
      setSelected(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      setDeleteModal(false);
      setDeleteTarget(null);
      flash("✅ Vendor deleted.");
    } catch (err) { flash("Error: " + err.message, true); }
    setDeleting(false);
  }

  // ── Bulk delete ───────────────────────────────────────────────────────────
  async function confirmBulkDelete() {
    if (selected.size === 0) return;
    setDeleting(true);
    const deletedCount = selected.size;
    try {
      const ids    = Array.from(selected);
      const chunks = [];
      for (let i = 0; i < ids.length; i += 500) chunks.push(ids.slice(i, i + 500));
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(id => batch.delete(doc(db, "companies", companyId, "users", id)));
        await batch.commit();
      }
      try {
        await updateDoc(doc(db, "companies", companyId), { vendor_count: increment(-deletedCount) });
      } catch (e) { console.error("[COUNTER] vendor_count bulk decrement failed:", e.message); }
      setVendors(prev => prev.filter(v => !selected.has(v.id)));
      flash(`✅ Deleted ${deletedCount} vendor(s).`);
      setSelected(new Set());
      setSelectMode(false);
      setBulkDeleteModal(false);
    } catch (err) { flash("Error: " + err.message, true); }
    setDeleting(false);
  }

  function toggleSelect(id) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleSelectAll() {
    setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map(v => v.id)));
  }

  function exitSelectMode() { setSelectMode(false); setSelected(new Set()); }

  function getSectionNames(ids) {
    if (!ids?.length) return [];
    // Only return names for sections that still exist — silently drop stale/deleted IDs
    return ids
      .map(id => sections.find(s => s.id === id)?.name ?? null)
      .filter(Boolean);
  }

  function formatTime(ts) {
    if (!ts) return "Unknown";
    const d    = ts.toDate ? ts.toDate() : new Date(ts);
    const diff = Math.floor((new Date() - d) / 60000);
    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString("en-GB");
  }

  function isOnline(ts) {
    if (!ts) return false;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return Math.floor((new Date() - d) / 60000) < 10;
  }

  const filtered      = vendors.filter(v =>
    `${v.first_name} ${v.last_name} ${v.name} ${v.phone} ${v.imei} ${v.plate}`
      .toLowerCase().includes(search.toLowerCase())
  );
  const activeCount   = vendors.filter(v => v.active !== false).length;
  const onlineCount   = vendors.filter(v => { const loc = liveLocations[v.id]; return loc && isOnline(loc.timestamp); }).length;
  const allFilteredSelected = filtered.length > 0 && filtered.every(v => selected.has(v.id));
  const vendorsWithLocation = vendors.filter(v => liveLocations[v.id]);

  if (loading) return <div className="bg-white rounded-xl shadow-sm p-6"><p className="text-gray-500 text-sm">{t("vendors_loading")}</p></div>;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("vendors_title")}</h2>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-gray-500 text-sm">{vendors.length} {t("vendors_total")}{maxVendors != null ? ` / ${maxVendors} max` : ""}</p>
            {activeCount > 0 && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{activeCount} {t("vendors_active")}</span>}
            {onlineCount > 0 && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">🟢 {onlineCount} {t("vendors_online")}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!selectMode ? (
            <button onClick={() => setSelectMode(true)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">☑️ {t("vendors_select")}</button>
          ) : (
            <button onClick={exitSelectMode} className="bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">{t("vendors_cancel")}</button>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t("vendors_live_map")}</span>
            <UISwitch value={showMap} onChange={setShowMap} />
          </div>
          <button onClick={openCreate} className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg">{t("vendors_add")}</button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-green-600 cursor-pointer" />
            <span className="text-sm text-blue-800 font-medium">
              {selected.size === 0 ? t("vendors_select_for_bulk") : `${selected.size} ${t("vendors_selected_count")}`}
            </span>
          </div>
          {selected.size > 0 && (
            <button onClick={() => setBulkDeleteModal(true)} className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
              🗑 {t("delete")} ({selected.size})
            </button>
          )}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-blue-700 text-sm">🔐 <strong>{t("vendors_auto_account")}:</strong> {t("vendors_auto_account_msg")}</p>
      </div>

      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      {/* Live Map */}
      {showMap && isLoaded && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">📍 {t("vendors_locations")}</h3>
            <p className="text-xs text-gray-400">{vendorsWithLocation.length} {t("vendors_with_location")}</p>
          </div>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "400px" }}
            center={vendorsWithLocation.length > 0 ? { lat: liveLocations[vendorsWithLocation[0].id].lat, lng: liveLocations[vendorsWithLocation[0].id].lng } : MAP_CENTER}
            zoom={vendorsWithLocation.length > 0 ? 12 : 10}
            options={{ styles: MAP_STYLES, zoomControl: true }}>
            {vendors.map(vendor => {
              const loc = liveLocations[vendor.id];
              if (!loc) return null;
              const online = isOnline(loc.timestamp);
              return (
                <Marker key={vendor.id} position={{ lat: loc.lat, lng: loc.lng }}
                  onClick={() => setSelectedMarker(vendor.id)}
                  icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: online ? "#22c55e" : "#94a3b8", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 }} />
              );
            })}
            {selectedMarker && (() => {
              const vendor = vendors.find(v => v.id === selectedMarker);
              const loc    = liveLocations[selectedMarker];
              if (!vendor || !loc) return null;
              return (
                <InfoWindow position={{ lat: loc.lat, lng: loc.lng }} onCloseClick={() => setSelectedMarker(null)}>
                  <div className="p-1 min-w-32">
                    <p className="font-semibold text-gray-800 text-sm">{vendor.first_name} {vendor.last_name}</p>
                    {vendor.plate && <p className="text-xs text-gray-500 mt-0.5">🚗 {vendor.plate}</p>}
                    <p className="text-xs text-gray-500 mt-0.5">🕐 {formatTime(loc.timestamp)}</p>
                    {loc.speed > 0 && <p className="text-xs text-gray-500">💨 {loc.speed} km/h</p>}
                    <p className={`text-xs font-medium mt-1 ${isOnline(loc.timestamp) ? "text-green-600" : "text-gray-400"}`}>
                      {isOnline(loc.timestamp) ? "🟢 Online" : "⚫ Offline"}
                    </p>
                  </div>
                </InfoWindow>
              );
            })()}
          </GoogleMap>
          <div className="px-5 py-3 bg-gray-50 flex gap-6 text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>{t("vendors_online_10min")}</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-400 inline-block"></span>{t("offline")}</span>
          </div>
        </div>
      )}

      {/* Search */}
      {vendors.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("vendors_search_ph")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
      )}

      {/* Vendors List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Truck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">{t("vendors_no_vendors")}</p>
          <button onClick={openCreate} className="mt-4 bg-green-600 text-white text-sm font-medium px-4 py-2 rounded-lg">{t("vendors_add")}</button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(vendor => {
            const loc        = liveLocations[vendor.id];
            const online     = loc && isOnline(loc.timestamp);
            const zoneNames  = getSectionNames(vendor.sections);
            const isSelected = selected.has(vendor.id);
            return (
              <div key={vendor.id} className={`bg-white rounded-xl shadow-sm p-5 transition ${isSelected ? "ring-2 ring-blue-400 bg-blue-50" : ""}`}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <div className="flex items-center gap-4">
                    {selectMode && (
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(vendor.id)}
                        className="w-4 h-4 accent-blue-600 cursor-pointer flex-shrink-0" />
                    )}
                    <div className="relative">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-lg ${vendor.active !== false ? "bg-green-500" : "bg-gray-400"}`}>
                        {(vendor.first_name || "?").charAt(0).toUpperCase()}
                      </div>
                      {loc && <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${online ? "bg-green-400" : "bg-gray-400"}`} />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-800">{vendor.first_name} {vendor.last_name}</p>
                        {vendor.uid && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">✓ Account</span>}
                        {loc && <span className={`text-xs px-2 py-0.5 rounded-full ${online ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-500"}`}>{online ? "🟢 Online" : `⚫ ${formatTime(loc.timestamp)}`}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1">
                        {vendor.email && <p className="text-gray-400 text-xs">✉️ {vendor.email}</p>}
                        {vendor.phone && <p className="text-gray-400 text-xs">📞 {vendor.phone}</p>}
                        {vendor.plate && <p className="text-gray-500 text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">🚗 {vendor.plate}</p>}
                        {vendor.imei  && <p className="text-gray-400 text-xs font-mono">📡 {vendor.imei}</p>}
                      </div>
                      {zoneNames.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {zoneNames.map((name, i) => <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">🗂 {name}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{vendor.active !== false ? t("active") : t("inactive")}</span>
                      <UISwitch value={vendor.active !== false} onChange={val => toggleActive(vendor, val)} />
                    </div>
                    <button onClick={() => openEdit(vendor)} className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-medium">{t("edit")}</button>
                    <button onClick={() => askDelete(vendor)} className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium">{t("delete")}</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Single Delete Modal ──────────────────────────────────────────── */}
      <Modal isOpen={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} title={t("vendors_delete_title")} size="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-red-600 text-lg">🗑</span>
            </div>
            <div>
              <p className="text-gray-800 font-medium">Delete "{deleteTarget?.first_name} {deleteTarget?.last_name}"?</p>
              <p className="text-gray-500 text-sm mt-1">{t("vendors_cannot_undo_full")}</p>
            </div>
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button onClick={confirmSingleDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
              {deleting ? t("deleting") : `🗑 ${t("yes_delete")}`}
            </button>
            <button onClick={() => { setDeleteModal(false); setDeleteTarget(null); }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">{t("cancel")}</button>
          </div>
        </div>
      </Modal>

      {/* ── Bulk Delete Modal ────────────────────────────────────────────── */}
      <Modal isOpen={bulkDeleteModal} onClose={() => setBulkDeleteModal(false)} title={t("vendors_bulk_delete")} size="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-lg">⚠️</span>
            </div>
            <div>
              <p className="text-gray-800 font-medium">Delete {selected.size} vendor{selected.size > 1 ? "s" : ""}?</p>
              <p className="text-gray-500 text-sm mt-1">{t("vendors_cannot_undo_full")}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
            {Array.from(selected).map(id => {
              const v = vendors.find(x => x.id === id);
              if (!v) return null;
              return <p key={id} className="text-xs text-gray-600">• {v.first_name} {v.last_name}{v.plate ? ` — ${v.plate}` : ""}</p>;
            })}
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button onClick={confirmBulkDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
              {deleting ? t("deleting") : `🗑 ${t("delete_all")} (${selected.size})`}
            </button>
            <button onClick={() => setBulkDeleteModal(false)} className="bg-gray-100 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">{t("cancel")}</button>
          </div>
        </div>
      </Modal>

      {/* ── Edit / Create Modal ──────────────────────────────────────────── */}
      <Modal isOpen={modalOpen} onClose={closeModal} title={editingVendor ? `${t("edit")} — ${editingVendor.first_name} ${editingVendor.last_name}` : t("vendors_new")} size="lg">
        <form onSubmit={handleSave} className="space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_first_name")} *</label>
              <input value={form.first_name} onChange={e => setForm(p => ({...p, first_name: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t("form_first_name")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_last_name")}</label>
              <input value={form.last_name} onChange={e => setForm(p => ({...p, last_name: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t("form_last_name")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_email")} * <span className="text-gray-400 text-xs">{t("form_email_login")}</span></label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="vendor@email.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("form_password")} {editingVendor ? <span className="text-gray-400 text-xs">{t("form_password_keep")}</span> : "*"}
              </label>
              <div className="relative">
                <input value={form.password} type={showPassword ? "text" : "password"}
                  onChange={e => setForm(p => ({...p, password: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 pr-16"
                  placeholder={editingVendor ? t("form_password_keep") : t("form_password_min")} />
                <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-2 top-2 text-xs text-gray-400 px-1">
                  {showPassword ? t("form_hide") : t("form_show")}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_phone")}</label>
              <input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="+971 50 000 0000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("vendors_plate")} *</label>
              <input value={form.plate} onChange={e => setForm(p => ({...p, plate: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="A 12345 Dubai" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("vendors_imei")} * <span className="text-gray-400 text-xs">{t("vendors_imei_hint")}</span></label>
              <input value={form.imei} onChange={e => setForm(p => ({...p, imei: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="359073012345678" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("vendors_assigned_zones")}</label>
              {sections.length === 0 ? <p className="text-sm text-gray-400 italic">{t("vendors_no_zones")}</p> : (
                <div className="flex flex-wrap gap-2">
                  {sections.map(section => {
                    const sel = form.sections.includes(section.id);
                    return (
                      <button key={section.id} type="button" onClick={() => toggleSection(section.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${sel ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-600 border-gray-300 hover:border-green-400"}`}>
                        {sel ? "✓ " : ""}{section.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_notes")}</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t("form_notes")} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("vendors_days_off")}</label>
              <div className="flex flex-wrap gap-2">
                {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      const current = form.off_days || [];
                      const updated = current.includes(day)
                        ? current.filter(d => d !== day)
                        : [...current, day];
                      setForm(p => ({ ...p, off_days: updated }));
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      (form.off_days || []).includes(day)
                        ? "bg-red-100 text-red-700 border border-red-300"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}>
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">{t("vendors_days_off_hint")}</p>
            </div>
            <div className="col-span-2 flex items-center gap-3 py-1">
              <label className="text-sm font-medium text-gray-700">{t("vendors_status")}</label>
              <UISwitch value={form.active} onChange={val => setForm(p => ({...p, active: val}))} />
              <span className="text-sm text-gray-500">{form.active ? t("active") : t("inactive")}</span>
            </div>
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button type="submit" disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
              {saving ? t("saving") : editingVendor ? t("vendors_save_changes") : t("vendors_create_vendor")}
            </button>
            <button type="button" onClick={closeModal} className="bg-gray-100 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">{t("cancel")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default VendorsManager;
